import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, Users, AlertTriangle, CheckCircle2, XCircle, 
  Clock, Target, TrendingUp, ExternalLink, ChevronDown, ChevronUp,
  Activity, Shield, Link2, FileText, Loader2, RefreshCw
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

// ==========================================
// MINI BAR CHART FOR MONTHLY DEMAND
// ==========================================
const MiniBarChart = ({ data, maxValue, label }) => {
  const months = Object.keys(data || {}).sort();
  const values = months.map(m => data[m] || 0);
  const max = maxValue || Math.max(...values, 1);
  
  if (months.length === 0) {
    return (
      <div className="text-xs text-gray-400 italic">No demand data</div>
    );
  }
  
  return (
    <div className="space-y-1">
      <div className="flex items-end gap-1 h-12">
        {months.slice(0, 6).map((month, idx) => {
          const value = values[idx] || 0;
          const height = Math.max((value / max) * 100, 4);
          return (
            <div key={month} className="flex flex-col items-center flex-1">
              <div
                className="w-full bg-purple-500 rounded-t transition-all"
                style={{ height: `${height}%` }}
                title={`${month}: ${value}h`}
              />
              <span className="text-[9px] text-gray-500 mt-1 truncate w-full text-center">
                {month.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
      {label && <span className="text-[10px] text-gray-500">{label}</span>}
    </div>
  );
};

// ==========================================
// ALIGNMENT STATUS INDICATOR
// ==========================================
const AlignmentIndicator = ({ 
  label, 
  value, 
  icon: Icon, 
  isBoolean = false,
  options = [],
  onChange,
  editable = false
}) => {
  const getStatusColor = () => {
    if (isBoolean) {
      return value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500';
    }
    switch (value) {
      case 'none': return 'bg-green-100 text-green-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      case 'minor': return 'bg-blue-100 text-blue-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'moderate': return 'bg-amber-100 text-amber-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'significant': return 'bg-orange-100 text-orange-700';
      case 'critical': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  const displayValue = isBoolean 
    ? (value ? 'Yes' : 'No') 
    : (value || 'None').charAt(0).toUpperCase() + (value || 'none').slice(1);

  return (
    <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-700">{label}</span>
      </div>
      {editable && !isBoolean ? (
        <select
          value={value || 'none'}
          onChange={(e) => onChange && onChange(e.target.value)}
          className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${getStatusColor()}`}
          data-testid={`alignment-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {options.map(opt => (
            <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
          ))}
        </select>
      ) : editable && isBoolean ? (
        <button
          onClick={() => onChange && onChange(!value)}
          className={`text-xs font-medium px-2 py-1 rounded-full transition-colors ${getStatusColor()}`}
          data-testid={`alignment-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {displayValue}
        </button>
      ) : (
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor()}`}>
          {displayValue}
        </span>
      )}
    </div>
  );
};

// ==========================================
// CONFLICT ALERT COMPONENT
// ==========================================
const ConflictAlert = ({ conflict }) => {
  const severityColors = {
    low: 'border-blue-200 bg-blue-50 text-blue-700',
    medium: 'border-amber-200 bg-amber-50 text-amber-700',
    high: 'border-orange-200 bg-orange-50 text-orange-700',
    critical: 'border-red-200 bg-red-50 text-red-700'
  };

  return (
    <div className={`p-3 rounded-lg border ${severityColors[conflict.severity] || severityColors.medium}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <span className="text-xs font-medium uppercase">{conflict.type.replace(/_/g, ' ')}</span>
          <p className="text-sm mt-1">{conflict.message}</p>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// LINKED SCOPE CARD
// ==========================================
const LinkedScopeCard = ({ linkedScope, linkedWbs, baselineVersion, onNavigate }) => {
  if (!linkedScope && !linkedWbs && !baselineVersion) {
    return (
      <div className="p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <div className="flex items-center gap-2 text-gray-500">
          <Link2 className="w-4 h-4" />
          <span className="text-sm">No linked scope or WBS</span>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Link this work item to a requirement or WBS node for traceability
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {linkedScope && (
        <div 
          className="p-3 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={() => onNavigate && onNavigate('requirement', linkedScope.requirementId)}
          data-testid="linked-requirement"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">{linkedScope.requirementName}</span>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <span className="text-xs text-blue-600">{linkedScope.requirementType}</span>
        </div>
      )}
      {linkedWbs && (
        <div 
          className="p-3 bg-purple-50 rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors"
          onClick={() => onNavigate && onNavigate('wbs', linkedWbs.wbsNodeId)}
          data-testid="linked-wbs"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">{linkedWbs.wbsNodeName}</span>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-purple-400" />
          </div>
          <span className="text-xs text-purple-600">{linkedWbs.wbsNodeType}</span>
        </div>
      )}
      {baselineVersion && (
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
          <Shield className="w-3.5 h-3.5" />
          <span>Baseline v{baselineVersion}</span>
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN WORK ITEM RESOURCE PANEL
// ==========================================
export const WorkItemResourcePanel = ({
  workItemId,
  tenantId,
  editable = false,
  onNavigate,
  className = ''
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [updating, setUpdating] = useState(false);

  const fetchData = useCallback(async () => {
    if (!workItemId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.getWorkItemResourceOverview(workItemId, tenantId);
      setData(response.data);
    } catch (err) {
      console.error('Failed to load resource overview:', err);
      setError(err.response?.data?.detail || 'Failed to load resource data');
    } finally {
      setLoading(false);
    }
  }, [workItemId, tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAlignmentChange = async (field, value) => {
    if (!editable || updating) return;
    
    setUpdating(true);
    try {
      await api.updateDeliveryAlignment(workItemId, {
        [field]: value,
        tenant_id: tenantId
      });
      
      // Update local state
      setData(prev => ({
        ...prev,
        deliveryAlignment: {
          ...prev.deliveryAlignment,
          [field]: value
        }
      }));
      
      toast.success('Alignment updated');
    } catch (err) {
      console.error('Failed to update alignment:', err);
      toast.error('Failed to update alignment');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center gap-3 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { demand, allocation, conflicts, deliveryAlignment, linkedScope, linkedWbs, baselineVersion } = data;

  return (
    <div 
      className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${className}`}
      data-testid="work-item-resource-panel"
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <BarChart3 className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">Resource Overview</h3>
            <p className="text-xs text-gray-500">Demand, allocation & delivery alignment</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Quick score indicator */}
          <div className={`px-2.5 py-1 rounded-full text-xs font-bold ${
            deliveryAlignment.overallScore >= 75 ? 'bg-green-100 text-green-700' :
            deliveryAlignment.overallScore >= 50 ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}>
            {deliveryAlignment.overallScore}%
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-5 pb-5 space-y-5">
          {/* Conflicts Alert */}
          {conflicts && conflicts.length > 0 && (
            <div className="space-y-2">
              {conflicts.map((conflict, idx) => (
                <ConflictAlert key={idx} conflict={conflict} />
              ))}
            </div>
          )}

          {/* Demand & Allocation Summary */}
          <div className="grid grid-cols-2 gap-4">
            {/* Role Demand Chart */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-gray-700">Role Demand</span>
              </div>
              <MiniBarChart 
                data={demand.roleDemandMonthly} 
                label={`Total: ${demand.totalRoleDemand}h`}
              />
            </div>

            {/* Team Demand Chart */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Team Demand</span>
              </div>
              <MiniBarChart 
                data={demand.teamDemandMonthly}
                label={`Total: ${demand.totalTeamDemand}h`}
              />
            </div>
          </div>

          {/* Allocation Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Allocation Summary</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{allocation.totalAllocated}h</span>
            </div>
            {allocation.allocations && allocation.allocations.length > 0 ? (
              <div className="space-y-1 mt-3">
                {allocation.allocations.slice(0, 3).map((alloc, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">{alloc.role_name || alloc.team_name || 'Unassigned'}</span>
                    <span className="font-medium">{alloc.allocated_hours || alloc.allocation_value}h</span>
                  </div>
                ))}
                {allocation.allocations.length > 3 && (
                  <span className="text-xs text-gray-400">+{allocation.allocations.length - 3} more</span>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic">No allocations assigned</p>
            )}
          </div>

          {/* Delivery Alignment Status */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Delivery Alignment Status</span>
              {updating && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <AlignmentIndicator
                label="AC Complete"
                value={deliveryAlignment.acComplete}
                icon={CheckCircle2}
                isBoolean
                editable={editable}
                onChange={(v) => handleAlignmentChange('acComplete', v)}
              />
              <AlignmentIndicator
                label="Dependencies Mapped"
                value={deliveryAlignment.dependenciesMapped}
                icon={Link2}
                isBoolean
                editable={editable}
                onChange={(v) => handleAlignmentChange('dependenciesMapped', v)}
              />
              <AlignmentIndicator
                label="Risk Status"
                value={deliveryAlignment.riskStatus}
                icon={AlertTriangle}
                options={['none', 'low', 'medium', 'high', 'critical']}
                editable={editable}
                onChange={(v) => handleAlignmentChange('riskStatus', v)}
              />
              <AlignmentIndicator
                label="Scope Drift"
                value={deliveryAlignment.scopeDrift}
                icon={Target}
                options={['none', 'minor', 'moderate', 'significant']}
                editable={editable}
                onChange={(v) => handleAlignmentChange('scopeDrift', v)}
              />
            </div>
          </div>

          {/* Linked Scope */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Linked Scope & WBS</span>
            </div>
            <LinkedScopeCard
              linkedScope={linkedScope}
              linkedWbs={linkedWbs}
              baselineVersion={baselineVersion}
              onNavigate={onNavigate}
            />
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={fetchData}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="refresh-resource-panel"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            <button
              onClick={() => onNavigate && onNavigate('utilisation')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              data-testid="view-utilisation-link"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View Utilisation
            </button>
            <button
              onClick={() => onNavigate && onNavigate('requirement-panel')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              data-testid="view-requirement-panel-link"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Requirement Panel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkItemResourcePanel;
